import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useLang } from "@/context/LanguageContext";
import { createCheckoutSession } from "@/lib/api";
import { toast } from "sonner";

export const OrderDialog = ({ open, onOpenChange, product, fitment: initialFitment, quantity: initialQty, options: initialOptions }) => {
  const { t, lang } = useLang();
  const PRICE = product?.price_eur ?? 349;
  const SHIPPING = product?.shipping_eur ?? 25;
  const FITMENTS = (product?.fitments ?? []).map((f) => f.code);
  const SLUG = product?.slug ?? "front-lip";
  const SKU = product?.sku ?? "CR-CMFL-F8X";
  const DEFAULT_FITMENT = FITMENTS[0] || "F82";

  const [form, setForm] = useState({
    customer_name: "",
    email: "",
    phone: "",
    car_model: "",
    fitment: initialFitment || DEFAULT_FITMENT,
    shipping_address: "",
    city: "",
    zip_code: "",
    country: "",
    quantity: initialQty || 1,
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((s) => ({
        ...s,
        fitment: initialFitment || DEFAULT_FITMENT,
        quantity: initialQty || s.quantity,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, initialFitment, initialQty, SLUG]);

  const total = PRICE * Number(form.quantity || 1) + SHIPPING;

  const change = (k) => (e) =>
    setForm((s) => ({ ...s, [k]: e.target?.value ?? e }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        product_slug: SLUG,
        quantity: Number(form.quantity),
        language: lang,
        options: initialOptions || undefined,
        origin_url: window.location.origin,
      };
      const res = await createCheckoutSession(payload);
      if (res.url) {
        toast.loading("Redirecting to secure checkout…");
        window.location.href = res.url;
        return;
      }
      throw new Error("No checkout URL");
    } catch (err) {
      toast.error(t("order.error"));
      setSubmitting(false);
    }
  };

  const FieldLabel = ({ children }) => (
    <label className="font-mono text-[10px] tracking-tech text-bone/60 block mb-1.5">
      {children}
    </label>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="order-dialog"
        className="bg-ink border-[#333] rounded-none max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <div className="font-mono text-[10px] tracking-tech text-wood mb-2">
            ORDER / {SKU}
          </div>
          <DialogTitle className="font-display text-3xl md:text-4xl tracking-brutal uppercase text-bone">
            {product?.name || t("offer.title")}
          </DialogTitle>
          <DialogDescription className="font-mono text-xs text-bone/60">
            {t("order.subtitle")}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4 pt-2">
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <FieldLabel>{t("order.name")}</FieldLabel>
              <Input
                required
                data-testid="order-name"
                value={form.customer_name}
                onChange={change("customer_name")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
            <div>
              <FieldLabel>{t("order.email")}</FieldLabel>
              <Input
                required
                type="email"
                data-testid="order-email"
                value={form.email}
                onChange={change("email")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
            <div>
              <FieldLabel>{t("order.phone")}</FieldLabel>
              <Input
                data-testid="order-phone"
                value={form.phone}
                onChange={change("phone")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
            <div>
              <FieldLabel>{t("order.car")}</FieldLabel>
              <Input
                required
                data-testid="order-car"
                value={form.car_model}
                onChange={change("car_model")}
                placeholder="e.g. BMW M4 F82 2017"
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
          </div>

          <div>
            <FieldLabel>FITMENT</FieldLabel>
            <div className={`grid gap-2 ${FITMENTS.length <= 3 ? "grid-cols-3" : "grid-cols-3 md:grid-cols-6"}`}>
              {FITMENTS.map((code) => (
                <button
                  type="button"
                  key={code}
                  onClick={() => setForm((s) => ({ ...s, fitment: code }))}
                  data-testid={`order-fitment-${code}`}
                  className={`font-mono text-[11px] tracking-tech py-3 border ${
                    form.fitment === code
                      ? "border-wood bg-wood/10 text-wood"
                      : "border-[#333] text-bone/60 hover:border-bone/40"
                  }`}
                >
                  {code}
                </button>
              ))}
            </div>
          </div>

          <div>
            <FieldLabel>{t("order.address")}</FieldLabel>
            <Input
              required
              data-testid="order-address"
              value={form.shipping_address}
              onChange={change("shipping_address")}
              className="bg-steel border-[#333] rounded-none font-mono"
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <FieldLabel>{t("order.city")}</FieldLabel>
              <Input
                required
                data-testid="order-city"
                value={form.city}
                onChange={change("city")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
            <div>
              <FieldLabel>{t("order.zip")}</FieldLabel>
              <Input
                required
                data-testid="order-zip"
                value={form.zip_code}
                onChange={change("zip_code")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
            <div>
              <FieldLabel>{t("order.country")}</FieldLabel>
              <Input
                required
                data-testid="order-country"
                value={form.country}
                onChange={change("country")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 items-end">
            <div>
              <FieldLabel>{t("order.qty")}</FieldLabel>
              <Input
                type="number"
                min={1}
                max={5}
                data-testid="order-qty"
                value={form.quantity}
                onChange={change("quantity")}
                className="bg-steel border-[#333] rounded-none font-mono"
              />
            </div>
            <div className="col-span-2 border border-[#333] p-3">
              <div className="font-mono text-[10px] tracking-tech text-bone/50">
                {t("order.total")}
              </div>
              <div className="font-display text-3xl text-wood mt-0.5">
                €{total}
              </div>
            </div>
          </div>

          <div>
            <FieldLabel>{t("order.notes")}</FieldLabel>
            <Textarea
              data-testid="order-notes"
              value={form.notes}
              onChange={change("notes")}
              rows={3}
              className="bg-steel border-[#333] rounded-none font-mono"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="order-submit"
            className="w-full bg-wood text-ink font-mono text-xs tracking-tech py-4 hover:bg-bone transition-colors disabled:opacity-50"
          >
            {submitting ? t("order.submitting") : t("order.submit")}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default OrderDialog;
